import { useEffect, useRef } from 'react';
import {
    containsSpecialChars,
    getCharacterLimitMessage,
    getSpecialCharacterMessage,
    showToastMessage,
    uuidv4,
} from '../../../../../../utils/commonFunctions';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import { checkDuplicateName, generateCustomName } from './Utils';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFilters from '../../../../hooks/useFilters';
import { CrossSvg, CheckIcon } from '../../../../../CommonComponents/svgs/Svgs';

//// ----------------------------
//// Filter Utilities
//// ----------------------------

interface FilterGroupInputProps {
    id?: string | null;
    inputValue: string;
    setInputValue: (v: string) => void;
    setRenamingTabId: (id: string | null) => void;
    setHideInput: (hide: boolean) => void;
    activeFilterTab: (id: string, meta: any) => void;
}

/**
 * FilterGroupInput
 * Input field for creating or renaming a filter group.
 * - Auto-sizes to text width
 * - Validates special characters and length
 * - Supports Enter/Escape keyboard actions
 */
const FilterGroupInput = ({
    id,
    inputValue,
    setInputValue,
    setRenamingTabId,
    setHideInput,
    activeFilterTab,
}: FilterGroupInputProps) => {
    // Refs used to measure text width and control the input element
    const spanRef = useRef<HTMLSpanElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Context / hooks
    const { scenario_id } = useUrlParams();
    const { consoleFilterGroups, setConsoleFilterGroups, patchFilterGroup } = useFilters();

    // Input sizing constraints
    const MAX_INPUT_WIDTH = 100;
    const MIN_CURSOR_WIDTH = 50;

    //// ----------------------------
    //// Effects
    //// ----------------------------
    useEffect(() => {
        if (!spanRef.current || !inputRef.current) return;

        // Update hidden span with current text to measure width and set input width
        spanRef.current.textContent = inputValue || '|';
        const textWidth = spanRef.current.offsetWidth;
        inputRef.current.style.width =
            Math.min(Math.max(textWidth, MIN_CURSOR_WIDTH), MAX_INPUT_WIDTH) + 'px';
    }, [inputValue]);

    // Handle click outside the wrapper
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                id ? exitRenameMode() : resetInput();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [id]);

    //// ----------------------------
    //// Validation helpers
    //// ----------------------------
    const handleNameChange = (value: string) => {
        if (containsSpecialChars(value)) {
            const message = getSpecialCharacterMessage();
            showToastMessage('info', message);
        } else if (value.length > 26) {
            const message = getCharacterLimitMessage(26);
            showToastMessage('info', message);
        } else {
            setInputValue(value);
        }
    };

    //// ----------------------------
    //// Keyboard and input handlers
    //// ----------------------------
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, id?: string | null) => {
        // Enter: finalize rename or create new
        if (e.key === 'Enter') {
            return id ? handleFinalizeRename(id) : createNewGroup();
        }

        // Escape: cancel rename or reset input
        if (e.key === 'Escape') {
            return id ? exitRenameMode() : resetInput();
        }
    };

    const resetInput = () => {
        setInputValue('');
        setHideInput(true);
    };

    const exitRenameMode = () => {
        setRenamingTabId(null);
        resetInput();
    };

    //// ----------------------------
    //// Rename existing group
    //// ----------------------------
    const handleFinalizeRename = (groupId: string) => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue) {
            if (checkDuplicateName(trimmedValue)) {
                showToastMessage('error', 'Filter group with same name already exists');
                return;
            }
        }

        const group = consoleFilterGroups.get(groupId);
        if (!group) return;

        const newName = trimmedValue || generateCustomName(consoleFilterGroups);
        const updatedGroup = { ...group, filterGroupName: newName };

        const onSuccess = () => {
            setConsoleFilterGroups((prev) => new Map(prev).set(groupId, updatedGroup));
            exitRenameMode();
        };

        patchFilterGroup('update', updatedGroup, onSuccess);
    };

    //// ----------------------------
    //// Create new group
    //// ----------------------------
    const createNewGroup = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue) {
            // Duplicate checks and user feedback
            if (checkDuplicateName(trimmedValue)) {
                showToastMessage('error', 'Filter group with same name already exists');
                return;
            }
        }

        const newId = uuidv4();
        const newGroup = {
            filterGroupId: newId,
            filterGroupName: trimmedValue || generateCustomName(consoleFilterGroups),
            isActive: true,
            isHeaderActive: true,
            scenarioId: scenario_id ?? '',
            FilterMetadata: {},
        };

        const onSuccess = () => {
            setConsoleFilterGroups((prev) => {
                const newMap = new Map(prev);
                newMap.set(newId, newGroup);
                return newMap;
            });

            resetInput();
            activeFilterTab(newId, {});
        };

        // API call to create new group with empty filters
        patchFilterGroup('create', newGroup, onSuccess);
    };

    //// ----------------------------
    //// Render
    //// ----------------------------
    return (
        <CustomToolTip title={inputValue} placement="top">
            <div ref={wrapperRef} className="filter_tab filter_group_input_wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    autoFocus={true}
                    onChange={(e) => handleNameChange(e.target.value)}
                    onKeyDown={(e) => handleInputKeyDown(e, id)}
                    placeholder=""
                    className="custom_filtergroup_input_box"
                />

                {/* Hidden span used to measure text width for auto-sizing the input */}
                <span ref={spanRef} className="filter_group_input_measure">
                    {inputValue || ''}
                </span>

                {/* Floating action buttons */}
                <div className="filter_group_action_buttons">
                    <button
                        type="button"
                        className="filter_group_action_btn confirm"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            id ? handleFinalizeRename(id) : createNewGroup();
                        }}
                        title="Confirm"
                    >
                        <CheckIcon />
                    </button>
                    <button
                        type="button"
                        className="filter_group_action_btn cancel"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            id ? exitRenameMode() : resetInput();
                        }}
                        title="Cancel"
                    >
                        <CrossSvg />
                    </button>
                </div>
            </div>
        </CustomToolTip>
    );
};

export default FilterGroupInput;